import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { ChevronLeft, ChevronRight, RotateCw } from "lucide-react";
import styled from "styled-components";
import { HoverTooltip } from "../conponents/HoverTooltip";
import type { MerchantDestination, MerchantNeighbors } from "./merchantNavigation";

const Navigation = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
`;
export const Destination = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	padding: 0;
	border: 1px solid transparent;
	border-radius: 6px;
	background: transparent;
	color: #737c89;
	cursor: pointer;
	transition: background .16s, color .16s, border-color .16s;
	&:hover:not(:disabled) { background: #e5edf6; border-color: #d4dfed; color: #2563a6; }
	&:focus-visible { outline: 2px solid #168bdf; outline-offset: 2px; }
	&:disabled { cursor: default; opacity: .3; }
	@media (pointer: coarse) { width: 40px; height: 40px; }
`;
export const TooltipContent = styled.div`
	max-width: min(300px, calc(100vw - 48px));
	color: white;
	font-size: 12px;
	line-height: 1.5;
	overflow-wrap: anywhere;
	strong { display: block; font-size: 13px; margin-top: 3px; }
`;

// Auto width on a fixed tooltip shrinks to the space to the RIGHT of its anchor,
// before HoverTooltip's translateX(-100%) moves it to the left.
export const navigationTooltipStyle = {
	width: "max-content",
	maxWidth: "min(328px, calc(100vw - 16px))",
	boxSizing: "border-box" as const,
};

export const MerchantNavigationBar = ({ neighbors, loading, disabled, error, onRetry, onNavigate }: {
	neighbors: MerchantNeighbors | null;
	loading: boolean;
	disabled: boolean;
	error: boolean;
	onRetry: () => void;
	onNavigate: (destination: MerchantDestination, direction: -1 | 1) => void;
}) => { useLocale(); return ((
	<Navigation role="group" aria-label={msg("merchantNavigation")} aria-busy={loading}>
		{error ? (
			<HoverTooltip align="end" tooltipStyle={navigationTooltipStyle} content={<TooltipContent>{msg("couldNotLoadNeighboringMerchantsClickToRetry")}</TooltipContent>}>
				<Destination type="button" disabled={disabled || loading} aria-label={msg("retryLoadingNeighboringMerchants")} onClick={onRetry}>
					<RotateCw size={17} />
				</Destination>
			</HoverTooltip>
		) : ([-1, 1] as const).map(direction => {
			const destination = direction === -1 ? neighbors?.previous : neighbors?.next;
			const label = direction === -1 ? msg("previousNeighbor") : msg("nextNeighbor");
			const crossesClient = destination && destination.client.id !== neighbors?.client?.id;
			const caption = crossesClient ? msg("templateClient_240673ee", { v0: label, v1: destination.client.name }) : msg("templateMerchant_47af7193", { v0: label });
			const unavailable = loading ? msg("loadingNeighboringMerchants") : (direction === -1 ? msg("noPreviousMerchant") : msg("noNextMerchant"));
			return (
				<HoverTooltip key={`${direction}:${destination?.merchant.id ?? "none"}`} align="end" tooltipStyle={navigationTooltipStyle} content={
					<TooltipContent>
						{destination && !loading ? <>
							<div style={{ opacity: .75 }}>{caption}</div>
							<strong>{destination.merchant.name}</strong>
							{!crossesClient && <div style={{ opacity: .75 }}>{destination.client.name}</div>}
						</> : unavailable}
					</TooltipContent>
				}>
					<Destination
						type="button"
						disabled={disabled || loading || !destination}
						aria-label={destination && !loading ? `${caption}: ${destination.merchant.name}` : unavailable}
						onClick={() => destination && onNavigate(destination, direction)}>
						{direction === -1 ? <ChevronLeft size={19} /> : <ChevronRight size={19} />}
					</Destination>
				</HoverTooltip>
			);
		})}
	</Navigation>
)); };
